import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import CustomIcon, { AssetTypes } from "../../../../../public/icons-v1_1";

export type PathPickBadgeColorVariant =
  | "gray"
  | "blue"
  | "green"
  | "yellow"
  | "red";

export type PathPickBadgeSizeVariant = "small" | "medium";

interface Props {
  // 이미 번역된 문자열을 받는다 (i18n 키를 받지 않는다)
  label: string;
  colorVariant?: PathPickBadgeColorVariant;
  size?: PathPickBadgeSizeVariant;
  // 라벨 앞 아이콘
  leadingIcon?: AssetTypes;
  // 라벨 뒤 아이콘 (예: 상세로 이동하는 chevron-right)
  trailingIcon?: AssetTypes;
  // 라벨 뒤에 구분선으로 붙는 짧은 추가 문구 (예: 채널 표기 ["N", "K"])
  suffixes?: string[];
  // 전달되면 배지 전체가 클릭 가능해진다
  onClick?: (event: React.MouseEvent) => void;
}

// 안내 박스(PathPickInlineNotice)도 같은 팔레트를 쓴다 — 배지와 색이 어긋나면 안 된다.
export const BADGE_COLORS: Record<
  PathPickBadgeColorVariant,
  { color: string; borderColor: string; background: string }
> = {
  gray: { color: "#444440", borderColor: "#D4D4D1", background: "#FCFCFB" },
  blue: { color: "#1272EF", borderColor: "#A8CEFA", background: "#F0F9FF" },
  green: { color: "#4C994F", borderColor: "#A9E0AB", background: "#F0FAEF" },
  yellow: { color: "#B27700", borderColor: "#F5C79B", background: "#FFF7ED" },
  red: { color: "#CC1D1D", borderColor: "#EE7D7A", background: "#FFEEEE" },
};

// 패스픽 공용 상태 배지 — 앞/뒤 아이콘, 클릭, 뒤에 붙는 추가 문구 셀을 지원한다.
function PathPickBadge({
  label,
  colorVariant = "gray",
  size = "small",
  leadingIcon,
  trailingIcon,
  suffixes,
  onClick,
}: Props) {
  const colors = BADGE_COLORS[colorVariant];
  const isSmall = size === "small";
  const isClickable = Boolean(onClick);
  const iconSize = isSmall ? 14 : 16;
  const textStyle = isSmall ? "caption-bold" : "body-sm-bold";
  const padding = isSmall ? "1px 4px" : "2px 6px";

  return (
    <Flex
      // 아이콘 색은 여기서 currentColor로 상속시킨다 (CustomIcon에 color를 넘기지 않는다)
      color={colors.color}
      // stretch + hidden이라야 suffix 구분선이 배지 높이를 채우고 모서리가 radius로 잘린다
      alignItems={"stretch"}
      overflow={"hidden"}
      borderRadius={"6px"}
      border={`0.5px solid ${colors.borderColor}`}
      background={colors.background}
      width={"fit-content"}
      flexShrink={0}
      {...(isClickable
        ? {
            as: "button" as const,
            type: "button" as const,
            cursor: "pointer",
            position: "relative",
            onClick,
            // 오버레이는 반투명이어야 한다 — 불투명하면 배지 라벨/아이콘을 덮어버린다.
            _hover: {
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: "rgba(0, 0, 0, 0.06)",
              },
            },
          }
        : {})}
    >
      <Flex
        alignItems={"center"}
        gap={"2px"}
        padding={padding}
        whiteSpace={"nowrap"}
      >
        {leadingIcon && (
          <CustomIcon src={leadingIcon} width={iconSize} height={iconSize} />
        )}
        <Text textStyle={textStyle} fontSize={isSmall ? "11px" : "12px"}>
          {label}
        </Text>
        {trailingIcon && (
          <CustomIcon src={trailingIcon} width={iconSize} height={iconSize} />
        )}
      </Flex>
      {suffixes?.map((suffix) => (
        <Flex
          key={suffix}
          alignItems={"center"}
          justifyContent={"center"}
          minWidth={isSmall ? "20px" : "24px"}
          padding={isSmall ? "1px 4px" : "2px 6px"}
          borderLeft={`0.5px solid ${colors.borderColor}`}
        >
          <Text
            textStyle={textStyle}
            whiteSpace={"nowrap"}
            fontSize={isSmall ? "11px" : "12px"}
          >
            {suffix}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
}

export default PathPickBadge;
